// src/utils/airtable.js
// Handles all communication with the Airtable API

import { getCurrentCounty } from '../config/counties';

// Resolved per-call (not at module load) so it always reflects the county
// for the subdomain the app is currently running on.
const getAirtableConfig = () => {
  const county = getCurrentCounty();
  const baseId = county?.airtableBaseId;
  const token = county?.airtableToken || process.env.REACT_APP_AIRTABLE_TOKEN;
  return {
    baseId,
    apiUrl: `https://api.airtable.com/v0/${baseId}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

// Generic fetch function for any table
const fetchTable = async (tableName, params = {}) => {
  const { apiUrl, headers } = getAirtableConfig();
  const url = new URL(`${apiUrl}/${encodeURIComponent(tableName)}`);
  url.searchParams.set('filterByFormula', '{Active}=1');
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) {
    throw new Error(`Airtable error: ${response.status} ${response.statusText}`);
  }
  const data = await response.json();
  return data.records.map(record => ({
    id: record.id,
    ...record.fields,
  }));
};

// Fetch all candidates sorted by Sort Order
export const fetchCandidates = async () => {
  const { apiUrl, headers } = getAirtableConfig();
  const url = new URL(`${apiUrl}/Candidates`);
  url.searchParams.set('sort[0][field]', 'Office');
  url.searchParams.set('sort[0][direction]', 'asc');
  url.searchParams.set('sort[1][field]', 'District');
  url.searchParams.set('sort[1][direction]', 'asc');
  url.searchParams.set('sort[2][field]', 'Last Name');
  url.searchParams.set('sort[2][direction]', 'asc');
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) throw new Error(`Airtable error: ${response.status}`);
  const data = await response.json();
  return data.records.map(r => ({ id: r.id, ...r.fields }));
};
// Fetch all races
export const fetchRaces = async () => {
  return fetchTable('Races');
};

// Fetch upcoming events sorted by date
export const fetchEvents = async () => {
  const { apiUrl, headers } = getAirtableConfig();
  const url = new URL(`${apiUrl}/Events`);
  url.searchParams.set('filterByFormula', `AND({Active}=1, IS_AFTER({Date}, DATEADD(TODAY(), -1, 'days')))`);
  url.searchParams.set('sort[0][field]', 'Date');
  url.searchParams.set('sort[0][direction]', 'asc');
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) throw new Error(`Airtable error: ${response.status}`);
  const data = await response.json();
  return data.records.map(r => ({ id: r.id, ...r.fields }));
};

// Fetch resources sorted by Sort Order
export const fetchResources = async () => {
  return fetchTable('Resources', {
    'sort[0][field]': 'Sort Order',
    'sort[0][direction]': 'asc',
  });
};

// Fetch ballot initiatives sorted by Sort Order
export const fetchBallotInitiatives = async () => {
  return fetchTable('Ballot Initiatives', {
    'sort[0][field]': 'Sort Order',
    'sort[0][direction]': 'asc',
  });
};

// Fetch active push notification subscribers. De-duped by token — a handful
// of legacy duplicate records could otherwise send the same person two
// notifications for one Send click.
export const fetchSubscribers = async () => {
  const { apiUrl, headers } = getAirtableConfig();
  const url = new URL(`${apiUrl}/Subscribers`);
  url.searchParams.set('filterByFormula', '{Active}=1');
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) throw new Error(`Airtable error: ${response.status}`);
  const data = await response.json();
  const tokens = data.records.map(r => r.fields.Token).filter(Boolean);
  return [...new Set(tokens)];
};

// Find an existing Subscribers record by token, regardless of Active status —
// used to avoid creating a duplicate record when a user re-enables
// notifications after being deleted or deactivated.
export const findSubscriberByToken = async (token) => {
  const { apiUrl, headers } = getAirtableConfig();
  const safeToken = token.replace(/"/g, '\\"');
  const url = new URL(`${apiUrl}/Subscribers`);
  url.searchParams.set('filterByFormula', `{Token}="${safeToken}"`);
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) throw new Error(`Airtable error: ${response.status}`);
  const data = await response.json();
  return data.records[0] || null;
};

// Reactivate an existing Subscribers record (sets Active back to true).
// Any extraFields (e.g. identity info) are merged in rather than replacing
// the record, so re-enabling doesn't wipe out what was captured before.
export const reactivateSubscriberToken = async (recordId, extraFields = {}) => {
  const { apiUrl, headers } = getAirtableConfig();
  const response = await fetch(`${apiUrl}/Subscribers/${recordId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ fields: { Active: true, ...extraFields } }),
  });
  if (!response.ok) throw new Error(`Airtable error: ${response.status}`);
  return response.json();
};

// Single entry point for "register this token" — used by every flow that can
// enable notifications (the auto-prompt, its identity form, and the manual
// re-enable button) so none of them can create a duplicate Subscribers row
// for a token that already exists.
export const upsertSubscriberToken = async (token, extraFields = {}) => {
  const existing = await findSubscriberByToken(token);
  if (existing) {
    return reactivateSubscriberToken(existing.id, extraFields);
  }
  return saveSubscriberToken(token, extraFields);
};

// Save a push notification subscriber token, with optional identity fields
export const saveSubscriberToken = async (token, extraFields = {}) => {
  const { apiUrl, headers } = getAirtableConfig();
  const response = await fetch(`${apiUrl}/Subscribers`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      records: [{
        fields: {
          Token: token,
          Platform: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop',
          Created: new Date().toISOString().split('T')[0],
          Active: true,
          ...extraFields,
        },
      }],
    }),
  });
  if (!response.ok) throw new Error(`Airtable error: ${response.status}`);
  return response.json();
};

// Submit a volunteer signup
export const submitVolunteer = async (volunteerData) => {
  const { apiUrl, headers } = getAirtableConfig();
  const response = await fetch(`${apiUrl}/Volunteers`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      records: [
        {
          fields: {
            'First Name': volunteerData.firstName,
            'Last Name': volunteerData.lastName,
            Email: volunteerData.email,
            Phone: volunteerData.phone,
            Interests: volunteerData.interests,
            'Signup Date': new Date().toISOString().split('T')[0],
          },
        },
      ],
    }),
  });
  if (!response.ok) throw new Error(`Airtable error: ${response.status}`);
  return response.json();
};

// Fetch voter info from Google Civic Information API
export const fetchVoterInfo = async (address) => {
  const API_KEY = process.env.REACT_APP_GOOGLE_CIVIC_API_KEY;
  const encodedAddress = encodeURIComponent(address);
  const response = await fetch(
    `https://www.googleapis.com/civicinfo/v2/voterinfo?key=${API_KEY}&address=${encodedAddress}`
  );
  if (!response.ok) throw new Error(`Civic API error: ${response.status}`);
  return response.json();
};

// Fetch representatives from Google Civic Information API
export const fetchRepresentatives = async (address) => {
  const API_KEY = process.env.REACT_APP_GOOGLE_CIVIC_API_KEY;

  let response = await fetch(
    `https://www.googleapis.com/civicinfo/v2/representatives?key=${API_KEY}&address=${encodeURIComponent(address)}&includeOffices=true`
  );

  if (response.status === 404) {
    const cityState = address.split(',').slice(-2).join(',').trim();
    response = await fetch(
      `https://www.googleapis.com/civicinfo/v2/representatives?key=${API_KEY}&address=${encodeURIComponent(cityState)}&includeOffices=true`
    );
  }

  if (response.status === 404) {
    return { officials: [], offices: [] };
  }

  if (!response.ok) throw new Error(`Civic API error: ${response.status}`);
  return response.json();
};

// Fetch polling locations from Google Civic Information API
export const fetchPollingLocations = async (address) => {
  const API_KEY = process.env.REACT_APP_GOOGLE_CIVIC_API_KEY;
  const encodedAddress = encodeURIComponent(address);
  const response = await fetch(
    `https://www.googleapis.com/civicinfo/v2/voterinfo?key=${API_KEY}&address=${encodedAddress}`
  );
  if (response.status === 400 || response.status === 404) {
    return { pollingLocations: [], dropOffLocations: [], earlyVoteSites: [] };
  }
  if (!response.ok) throw new Error(`Civic API error: ${response.status}`);
  return response.json();
};

// Call our Netlify serverless function for voter lookup
export const lookupVoter = async (voterData) => {
  const response = await fetch('/.netlify/functions/voter-lookup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(voterData),
  });
  if (!response.ok) throw new Error(`Lookup error: ${response.status}`);
  return response.json();
};

// Fetch elected officials by district from Airtable
export const fetchOfficialsByDistrict = async (districts) => {
  const { apiUrl, headers } = getAirtableConfig();
  const url = new URL(`${apiUrl}/Officials`);
  const validDistricts = districts.filter(d => d);

  let formula;
  if (validDistricts.length === 0) {
    formula = `AND({Active}=1, {District Type} = "Statewide")`;
  } else {
    const districtFilters = validDistricts
      .flatMap(d => {
        if (d.startsWith('CO-')) return [`{District} = "${d}"`, `{District} = "CD-${d.slice(3)}"`];
        if (d.startsWith('CD-')) return [`{District} = "${d}"`, `{District} = "CO-${d.slice(3)}"`];
        return [`{District} = "${d}"`];
      })
      .join(', ');
    formula = `AND({Active}=1, OR(${districtFilters}, {District Type} = "Statewide"))`;
  }

  url.searchParams.set('filterByFormula', formula);
  url.searchParams.set('sort[0][field]', 'Level');
  url.searchParams.set('sort[0][direction]', 'asc');

  const response = await fetch(url.toString(), { headers });

  if (!response.ok) {
    const errorText = await response.text();
    console.log('Airtable error:', errorText);
    throw new Error(`Airtable error: ${response.status}`);
  }

  const data = await response.json();
  return data.records.map(r => ({ id: r.id, ...r.fields }));
};
